import { useEffect, useState } from "react";
import {
  Bell, CircleNotch, ArrowsClockwise, CheckCircle, Warning,
  Envelope, ClockCounterClockwise, PauseCircle, PlayCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function AdminAlerts() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/alerts/stats");
      setStats(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/admin/alerts/run-now", {});
      const s = r.data;
      toast.success(
        `Cycle complete — evaluated ${s.evaluated}, fired ${s.fired}, ${s.delivery_failures} failures`
      );
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Run failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div data-testid="admin-alerts-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#3B5BDB] font-semibold flex items-center gap-2">
            <Bell size={14} weight="duotone" /> Price alerts
          </p>
          <h1 className="font-display text-3xl sm:text-4xl mt-2 text-[#0A1128]">
            Email price alert engine
          </h1>
          <p className="mt-2 text-sm text-[#4A5568] max-w-2xl">
            Background loop checks active alerts every <strong>{stats?.interval_sec || 600}s</strong> and
            emails the user when the city/metal rate crosses their target. Once fired, the alert is
            paused until the user re-enables it.
          </p>
        </div>
        <button
          data-testid="alerts-run-now"
          onClick={runNow}
          disabled={running}
          className="px-4 py-2.5 rounded-md bg-[#3B5BDB] text-white font-medium hover:bg-[#2F49AF] smooth flex items-center gap-2 text-sm disabled:opacity-60"
        >
          {running ? (
            <CircleNotch size={16} weight="bold" className="animate-spin" />
          ) : (
            <ArrowsClockwise size={16} weight="bold" />
          )}
          {running ? "Running…" : "Run now"}
        </button>
      </div>

      {/* Resend status banner */}
      {stats && !stats.resend_configured && (
        <div className="mt-5 p-4 rounded-lg border border-amber-300 bg-amber-50 flex items-start gap-3" data-testid="alerts-no-resend">
          <Warning size={18} weight="duotone" className="text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-900">
            <p className="font-semibold">Resend is not configured.</p>
            <p className="mt-1">
              The loop runs and tracks runs, but no emails are sent. Add{" "}
              <code className="px-1 py-0.5 bg-white rounded text-[12px]">RESEND_API_KEY</code> in{" "}
              <code className="px-1 py-0.5 bg-white rounded text-[12px]">backend/.env</code> (or via
              Settings) and restart the backend to enable delivery.
            </p>
          </div>
        </div>
      )}
      {stats?.resend_configured && (
        <div className="mt-5 p-4 rounded-lg border border-emerald-300 bg-emerald-50 flex items-center gap-3">
          <CheckCircle size={18} weight="duotone" className="text-emerald-600" />
          <p className="text-sm text-emerald-900 font-medium">Resend configured — emails will be delivered.</p>
        </div>
      )}

      {/* KPI cards */}
      <div className="mt-5 grid sm:grid-cols-3 gap-3">
        <KpiCard
          icon={<PlayCircle size={18} weight="duotone" />}
          label="Active alerts"
          value={loading ? "…" : stats?.active ?? 0}
          tone="primary"
          testid="alerts-kpi-active"
        />
        <KpiCard
          icon={<PauseCircle size={18} weight="duotone" />}
          label="Paused / fired"
          value={loading ? "…" : stats?.paused ?? 0}
          tone="muted"
          testid="alerts-kpi-paused"
        />
        <KpiCard
          icon={<Envelope size={18} weight="duotone" />}
          label="Triggered today"
          value={loading ? "…" : stats?.triggered_today ?? 0}
          tone="success"
          testid="alerts-kpi-today"
        />
      </div>

      {/* Recent runs */}
      <div className="mt-6 mc-card p-5">
        <p className="text-xs uppercase tracking-[0.25em] text-[#4A5568] font-semibold flex items-center gap-2">
          <ClockCounterClockwise size={14} weight="duotone" /> Recent cycles
        </p>
        {loading ? (
          <div className="mt-4 flex items-center gap-2 text-sm text-[#4A5568]">
            <CircleNotch size={14} weight="bold" className="animate-spin" /> Loading…
          </div>
        ) : !stats?.recent_runs?.length ? (
          <p className="mt-4 text-sm text-[#4A5568]">
            No cycles recorded yet. The first cycle runs ~15 seconds after backend startup, then every {stats?.interval_sec || 600}s.
          </p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm" data-testid="alerts-runs-table">
              <thead className="text-left text-xs uppercase tracking-wider text-[#4A5568]">
                <tr className="border-b border-[#E8DFD1]">
                  <th className="py-2 pr-3">Started</th>
                  <th className="py-2 pr-3">Evaluated</th>
                  <th className="py-2 pr-3">Fired</th>
                  <th className="py-2 pr-3">Delivery fails</th>
                  <th className="py-2 pr-3">Skipped</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_runs.map((r, i) => (
                  <tr key={i} className="border-b border-[#E8DFD1]/60 last:border-0">
                    <td className="py-2 pr-3 text-[#0A1128]">{new Date(r.started_at).toLocaleString()}</td>
                    <td className="py-2 pr-3">{r.evaluated}</td>
                    <td className="py-2 pr-3">
                      <span className={r.fired > 0 ? "text-emerald-700 font-semibold" : "text-[#4A5568]"}>
                        {r.fired}
                      </span>
                    </td>
                    <td className="py-2 pr-3">
                      <span className={r.delivery_failures > 0 ? "text-red-700 font-semibold" : "text-[#4A5568]"}>
                        {r.delivery_failures}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-[#4A5568]">{r.skipped_unconfigured}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, tone = "primary", testid }) {
  const toneClasses = {
    primary: "bg-[#3B5BDB]/10 text-[#3B5BDB]",
    muted: "bg-[#F5EFE6] text-[#4A5568]",
    success: "bg-emerald-50 text-emerald-700",
  }[tone];
  return (
    <div className="mc-card p-5" data-testid={testid}>
      <div className="flex items-center gap-2">
        <span className={`w-8 h-8 rounded-md flex items-center justify-center ${toneClasses}`}>{icon}</span>
        <p className="text-xs uppercase tracking-wider text-[#4A5568]">{label}</p>
      </div>
      <p className="mt-3 font-display text-3xl text-[#0A1128]">{value}</p>
    </div>
  );
}
